import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { EnvironmentService } from '../services/environment.service';
import { FormDeactivateService } from '../services/form-deactivate.service';
import { UnsavedChangesModalComponent } from '../../shared/components/unsaved-changes-modal/unsaved-changes-modal.component';

/**
 * Deactivation guard for the form route.
 *
 * For Available Forms, FormIframeComponent registers a canDeactivate handler
 * with FormDeactivateService on init and clears it on destroy. The guard
 * delegates to that service so no @ViewChild chain is needed.
 *
 * When the `unsavedChangesModal` feature flag is disabled (current default),
 * navigation is always allowed immediately for the fallback path.
 *
 * When the flag is enabled the guard opens `UnsavedChangesModalComponent`.
 * The user can choose to:
 *  - Discard changes → allow navigation (return true)
 *  - Cancel          → stay on the form (return false)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formCanDeactivateGuard: CanDeactivateFn<any> = () => {
  // inject() calls MUST be at the top — before any conditional branches.
  const env             = inject(EnvironmentService);
  const modal           = inject(NgbModal);
  const formDeactivate  = inject(FormDeactivateService);

  // If FormIframeComponent registered a handler (Available Forms), delegate to it.
  if (formDeactivate.hasHandler) {
    return formDeactivate.run();
  }

  // Fallback: feature-flag controlled generic modal (To-Do / Completed forms or
  // when the component hasn't registered a handler yet).
  if (!env.features.unsavedChangesModal) {
    return true;
  }

  const modalRef = modal.open(UnsavedChangesModalComponent, { centered: true });
  return from(modalRef.result).pipe(
    map((result: string) => result === 'discard'),
    catchError(() => of(false)),
  );
};
